import { AxiosResponse } from "axios";
import { environments } from "environments/environments";
import { FormRegistrationPayload } from "pages/FeeManagement/components/Registration/FormRegistration/interfaces";
import {
    FeeDetranQueryDTO,
    FeeDetranResponseDTO,
    FeeRegistration,
    FeeRegistrationQueryDTO,
} from "pages/FeeManagement/types";
import { DocumentTypes } from "pages/Report/types";
import api from "./api";

const {
    ENDPOINT_API_TAXAS,
    ENDPOINT_API_OPERACOES_TAXAS,
    ENDPOINT_API_TABELA_TAXAS,
    ENDPOINT_API_DETRANS,
} = environments;

interface Query {
    cnpj: string;
    status: string;
    numeroTaxa: string;
    tipo?: DocumentTypes;
}

const getFee = async ({
    pagina,
    limite,
    query,
}: FeeDetranQueryDTO): Promise<AxiosResponse<FeeDetranResponseDTO>> => {
    return api.get(
        `${ENDPOINT_API_TAXAS}/?${new URLSearchParams(
            Object.assign(query ? { ...query, pagina, limite } : { pagina, limite }),
        )}`,
    );
};

const getCSV = (query: Query) => {
    return api.get(
        `${ENDPOINT_API_TAXAS}/relatorios/?${new URLSearchParams(
            Object.assign(query ? { ...query } : {}),
        )}`,
    );
};

const uploadFee = (data: FormData) => {
    return api.post(`${ENDPOINT_API_TAXAS}`, data, {
        headers: {
            Authorization: `Bearer ${sessionStorage.getItem("idToken")}`,
            "Content-Type": "multipart/form-data",
        },
    });
};

const getFeeRegistration = async ({
    pagina,
    limite,
    query,
}: FeeRegistrationQueryDTO): Promise<AxiosResponse<FeeRegistration[]>> => {
    return api.get(
        `${ENDPOINT_API_OPERACOES_TAXAS}/?${new URLSearchParams(
            Object.assign(query ? { ...query, pagina, limite } : { pagina, limite }),
        )}`,
    );
};

const getTaxRegistration = async ({
    pagina,
    limite,
    query,
}: FeeRegistrationQueryDTO): Promise<AxiosResponse<FeeRegistration[]>> => {
    return api.get(
        `${ENDPOINT_API_TABELA_TAXAS}/?${new URLSearchParams(
            Object.assign(query ? { ...query, pagina, limite } : { pagina, limite }),
        )}`,
    );
};

const createFeeRegistration = async (
    data: FormRegistrationPayload,
): Promise<AxiosResponse<FeeRegistration[]>> => {
    return api.post(`${ENDPOINT_API_OPERACOES_TAXAS}`, { ...data });
};

const editFeeRegistration = async (
    data: FormRegistrationPayload,
    guid: string,
): Promise<AxiosResponse<FeeRegistration[]>> => {
    return api.put(`${ENDPOINT_API_OPERACOES_TAXAS}/${guid}`, { ...data });
};

const createTaxRegistration = async (
    data: FormRegistrationPayload,
): Promise<AxiosResponse<FeeRegistration[]>> => {
    return api.post(`${ENDPOINT_API_TABELA_TAXAS}`, { ...data });
};

const editTaxRegistration = async (
    data: FormRegistrationPayload,
    guid: string,
): Promise<AxiosResponse<FeeRegistration[]>> => {
    return api.put(`${ENDPOINT_API_TABELA_TAXAS}/${guid}`, { ...data });
};

const deleteFeeRegistration = async (guid: string) => {
    return api.delete(`${ENDPOINT_API_OPERACOES_TAXAS}/${guid}`);
};

interface QueryRegistration {
    uf: string;
    dataInicio: string;
    dataFim: string;
    status: string;
    cnpjAgenteFinanceiro?: string;
    publica?: string;
    tipoVeiculo?: string;
    tipo?: DocumentTypes;
}

const getFileRegistration = (query: QueryRegistration) => {
    return api.get(
        `${ENDPOINT_API_OPERACOES_TAXAS}/relatorios/?${new URLSearchParams(
            Object.assign(query ? { ...query } : {}),
        )}`,
    );
};

const getFileTaxRegistration = (query: QueryRegistration) => {
    return api.get(
        `${ENDPOINT_API_TABELA_TAXAS}/relatorios/?${new URLSearchParams(
            Object.assign(query ? { ...query } : {}),
        )}`,
    );
};

interface DownloadGuiaPagamentoParams {
    mesDebito?: number;
    anoDebito?: number;
    debitos?: string;
    periodoInicio?: string;
    periodoFim?: string;
    quantidadeTaxas?: number;
}

interface GuiaRNParams {
    financeiraCnpj: string;
    mesReferencia: number;
    anoReferencia: number;
}

const removeEmptyValues = (params: Record<string, any>) => {
    return Object.fromEntries(
        Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== "",
        ),
    );
};

const getFeeDetranRS = async ({
    pagina,
    limite,
    query,
}: FeeDetranQueryDTO): Promise<AxiosResponse<FeeDetranResponseDTO>> => {
    const params = removeEmptyValues({
        cnpj: query?.cnpj,
        status: query.status,
        numeroTaxa: query.numeroTaxa,
        pagina,
        limite,
    });

    return api.get(
        `${ENDPOINT_API_DETRANS}/detrans/rs/taxas?${new URLSearchParams(params)}`,
    );
};

const downloadGuiaPagamentoTxt = async (
    cnpj: string | undefined,
    uf: string,
    params?: DownloadGuiaPagamentoParams,
): Promise<AxiosResponse<Blob>> => {
    const ufPath = uf.toLowerCase();
    const url = `${ENDPOINT_API_TAXAS}/${ufPath}`;

    const requestParams: Record<string, string | number> = {};

    if (cnpj) {
        requestParams.financeiraCnpj = cnpj;
    }

    if (uf === "ES") {
        requestParams.mesDebito = params?.mesDebito ?? "";
        requestParams.anoDebito = params?.anoDebito ?? "";
    }

    if (uf === "PR") {
        requestParams.periodoInicio = params?.periodoInicio ?? "";
        requestParams.periodoFim = params?.periodoFim ?? "";
    }

    if (uf === "TO" && params?.debitos) {
        requestParams.debitos = params.debitos;
    }

    if (uf === "RS" && params?.quantidadeTaxas !== undefined) {
        requestParams.quantidadeTaxas = params.quantidadeTaxas;
    }

    return api.get(url, {
        params: requestParams,
        responseType: "blob",
        headers: {
            Accept: "*/*",
        },
    });
};

const downloadRemessaRN = (params: GuiaRNParams): Promise<AxiosResponse<Blob>> => {
    return api.get(`${ENDPOINT_API_DETRANS}/detrans/rn/guias/download-txt`, {
        params,
        responseType: "blob",
        headers: { Accept: "text/plain" },
    });
};

const regerarDebitoRN = (financeiraCnpj: string, referencia: string): Promise<AxiosResponse<string>> => {
    return api.post(`${ENDPOINT_API_DETRANS}/detrans/rn/guias/regerar`, null, {
        params: { financeiraCnpj, referencia },
    });
};

const ServiceFee = {
    getFee,
    getFeeRegistration,
    getTaxRegistration,
    getCSV,
    uploadFee,
    createFeeRegistration,
    editFeeRegistration,
    createTaxRegistration,
    editTaxRegistration,
    deleteFeeRegistration,
    getFileRegistration,
    getFileTaxRegistration,
    downloadGuiaPagamentoTxt,
    downloadRemessaRN,
    regerarDebitoRN,
    getFeeDetranRS,
};

export default ServiceFee;
